/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: OutboundRouteFormView.tsx
 🗓️ Created: 2025-01-14
 🧩 Description:
    Form view for creating and editing Outbound Routes with 4 tabs.
    Manages routing strategies, patterns, and gateway associations.

 🔧 Notes:
    - 4 tabs: General, Patterns, Gateways, Advanced
    - Drag & drop for patterns and gateways
    - Domain-scoped data

 🔁 Last Updated: 2025-01-14 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useDebugMode } from '../../hooks/useDebugMode';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { Menu, GripVertical, Plus, Trash2, ArrowRightLeft, List, Search, Copy } from 'lucide-react';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import PinListSelect from '../../components/ui/PinListSelect';
import {
  OutboundRoute,
  OutboundRoutePattern,
  OutboundRouteGateway,
  OutboundRouteFormState,
  RoutingStrategy
} from './types';
import {
  DEFAULT_FORM_DATA,
  newPattern,
  newGateway,
  clonePatterns,
  cloneGateways,
  resequencePatterns,
  resequenceGateways,
  hasFormChanges
} from './utils';
import {
  fetchOutboundRoute,
  createOutboundRoute,
  updateOutboundRoute
} from './api/outboundRouteApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  deleteOutboundRouteById,
  navigateToOutboundRouteDuplicate,
} from './outboundRoutesActions';
import { OutboundRouteTabs } from './components/OutboundRouteTabs';
import { OutboundRouteGeneralSection } from './components/OutboundRouteGeneralSection';


export default function OutboundRouteFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo, showWarning } = useNotifications();
  const { isActive: debugModeActive } = useDebugMode();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('patterns');
  const lastSaveTimestamp = useRef<number>(0);

  // Form data
  const [formData, setFormData] = useState<OutboundRouteFormState>({ ...DEFAULT_FORM_DATA });

  // Patterns and Gateways
  const [patterns, setPatterns] = useState<OutboundRoutePattern[]>([]);
  const [gateways, setGateways] = useState<OutboundRouteGateway[]>([]);
  const [availableGateways, setAvailableGateways] = useState<Array<{ id: string | number; name: string; description?: string }>>([]);

  // Track original data to allow reset
  const [originalData, setOriginalData] = useState<OutboundRouteFormState>({ ...DEFAULT_FORM_DATA });
  const [originalPatterns, setOriginalPatterns] = useState<OutboundRoutePattern[]>([]);
  const [originalGateways, setOriginalGateways] = useState<OutboundRouteGateway[]>([]);

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Quick List state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<OutboundRoute[]>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevent drag activation on normal clicks
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const quickListLoadingRef = useRef(false);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData({ ...DEFAULT_FORM_DATA });
      setPatterns([]);
      setGateways([]);
      setOriginalData({ ...DEFAULT_FORM_DATA });
      setOriginalPatterns([]);
      setOriginalGateways([]);
      setErrors({});

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, resetParam, searchParams]);

  // Load route data if editing
  useEffect(() => {
    if (isEditMode && id && id !== 'new') {
      loadRoute(id);
    }
    loadAvailableGateways();
  }, [id, isEditMode]);

  // Add default pattern and gateway when creating a new route
  // This runs after the reset effect to ensure clean state
  useEffect(() => {
    if (isEditMode || availableGateways.length === 0) return;

    // Only add defaults if we're in create mode and form is empty
    if (patterns.length === 0 && gateways.length === 0) {
      setPatterns([newPattern(1)]);

      const defaultGateway = availableGateways[0];
      if (defaultGateway) {
        setGateways([
          {
            ...newGateway(1),
            // Store as-is (may be Sqid string or number)
            gatewayId: Number(defaultGateway.id),
            gatewayName: defaultGateway.name || '',
          },
        ]);
      }
    }
  }, [isEditMode, availableGateways.length, patterns.length, gateways.length]);

  const loadRoute = async (routeId?: string | null) => {
    const targetId = routeId ?? id;
    if (!targetId || targetId === 'new') return;

    setIsLoading(true);
    try {
      const route = await fetchOutboundRoute(targetId);

      if (route) {
        const loadedFormData = {
          ...DEFAULT_FORM_DATA,
          name: route.name || '',
          description: route.description || '',
          routingStrategy: route.routingStrategy || 'failover',
          priority: route.priority ?? 100,
          enabled: route.enabled ?? true,
          failoverModule: route.failoverModule || 'terminate_call',
          failoverDestination: route.failoverDestination || 'NORMAL_CLEARING',
          pinListId: route.pinListId || null,
          dialProfileId: route.dialProfileId || null,
        };

        if (duplicateId) {
          const duplicatedFormData = {
            ...loadedFormData,
            name: loadedFormData.name ? `${loadedFormData.name} (Copy)` : '',
          };
          setFormData(duplicatedFormData);
          setOriginalData({ ...DEFAULT_FORM_DATA });
        } else {
          setFormData(loadedFormData);
          setOriginalData(loadedFormData);
        }

        if (Array.isArray(route.patterns)) {
          const loadedPatterns = resequencePatterns(
            route.patterns.map((p: OutboundRoutePattern) => ({
              id: p.id,
              pattern: p.pattern || '',
              prepend: p.prepend || '',
              stripDigits: p.stripDigits || 0,
              description: p.description || '',
              priority: p.priority ?? 0,
              enabled: p.enabled ?? true,
            })),
          );
          setPatterns(loadedPatterns);
          setOriginalPatterns(duplicateId ? [] : clonePatterns(loadedPatterns));
        } else {
          setPatterns([]);
          setOriginalPatterns([]);
        }

        if (Array.isArray(route.gateways)) {
          const loadedGateways = resequenceGateways(
            route.gateways.map((g: OutboundRouteGateway & { gateway?: { name?: string } }) => ({
              id: g.id,
              gatewayId: g.gatewayId,
              gatewayName: g.gateway?.name || '',
              priority: g.priority ?? 0,
              weight: g.weight || 0,
              buyRate: g.buyRate || 0,
              sellRate: g.sellRate || 0,
              enabled: g.enabled ?? true,
              description: g.description || '',
            })),
          );
          setGateways(loadedGateways);
          setOriginalGateways(duplicateId ? [] : cloneGateways(loadedGateways));
        } else {
          setGateways([]);
          setOriginalGateways([]);
        }
      }
    } catch (error) {
      showError(t('outboundRoutes.messages.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableGateways = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'name',
        sortOrder: 'asc'
      });

      const response = await fetch(
        buildApiUrl(`/api/gateways?${queryParams}`),
        {
          method: 'GET',
          headers: getApiHeaders()
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setAvailableGateways(result.data);
        } else {
          throw new Error('Invalid response format');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'GATEWAYS_LOAD_FAILED');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('outboundRoutes.messages.gatewayLoadError');
      showError(message);
      console.error('Error loading gateways:', error);
    }
  };

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = t('outboundRoutes.validation.nameRequired');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('outboundRoutes.validation.nameMinLength');
    }

    if (patterns.length === 0) {
      newErrors.patterns = t('outboundRoutes.validation.patternRequired');
    } else if (patterns.some(pattern => !pattern.pattern.trim())) {
      newErrors.patterns = t('outboundRoutes.validation.patternValueRequired');
    }

    if (gateways.length === 0) {
      newErrors.gateways = t('outboundRoutes.validation.gatewayRequired');
    } else {
      // Check if all gateways have a valid gateway selected
      const hasInvalidGateway = gateways.some(g => !g.gatewayId || g.gatewayId === 0);
      if (hasInvalidGateway) {
        newErrors.gateways = t('outboundRoutes.validation.gatewaySelectionRequired');
      }
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [formData.name, patterns, gateways, t]);

  // Handle form submission
  const handleSubmit = useCallback(async (e?: React.FormEvent): Promise<OutboundRoute | undefined> => {
    e?.preventDefault();

    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
      showError(`${t('common.validationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    const orderedPatterns = resequencePatterns(clonePatterns(patterns));
    const orderedGateways = resequenceGateways(cloneGateways(gateways));

    const sanitizedFormData = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
    };

    const payload = {
      ...sanitizedFormData,
      patterns: orderedPatterns.map((pattern) => ({
        pattern: pattern.pattern.trim(),
        prepend: pattern.prepend ? pattern.prepend.trim() : null,
        stripDigits: Number.isFinite(pattern.stripDigits)
          ? Math.max(pattern.stripDigits, 0)
          : 0,
        description: pattern.description ? pattern.description.trim() : null,
        priority: pattern.priority,
        enabled: pattern.enabled,
      })),
      gateways: orderedGateways.map((gateway) => ({
        gatewayId: gateway.gatewayId,
        priority: gateway.priority,
        weight: Number.isFinite(gateway.weight)
          ? Math.max(gateway.weight, 0)
          : 0,
        buyRate: Number.isFinite(gateway.buyRate)
          ? Math.max(gateway.buyRate, 0)
          : 0,
        sellRate: Number.isFinite(gateway.sellRate)
          ? Math.max(gateway.sellRate, 0)
          : 0,
        enabled: gateway.enabled,
        description: gateway.description ? gateway.description.trim() : null,
      })),
    };

    setIsSaving(true);
    try {
      let result;
      if (isEditMode && id) {
        result = await updateOutboundRoute(id, payload);
      } else {
        result = await createOutboundRoute(payload);
      }

      const now = Date.now();
      if (now - lastSaveTimestamp.current >= 1000) {
        lastSaveTimestamp.current = now;
        showSuccess(
          isEditMode
            ? t('outboundRoutes.messages.updateSuccess')
            : t('outboundRoutes.messages.createSuccess'),
        );
      }

      setFormData(sanitizedFormData);
      setPatterns(orderedPatterns);
      setGateways(orderedGateways);
      setOriginalData({ ...sanitizedFormData });
      setOriginalPatterns(clonePatterns(orderedPatterns));
      setOriginalGateways(cloneGateways(orderedGateways));

      // Return saved data for useSaveWithAction
      return result;
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('outboundRoutes.messages.createError');
      showError(msg);
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }, [
    validateForm,
    formData,
    originalData,
    patterns,
    originalPatterns,
    gateways,
    originalGateways,
    isEditMode,
    id,
    showSuccess,
    showError,
    showInfo,
    t,
  ]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as OutboundRoute;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/calls-routing/outbound',
    newPath: '/pbx/calls-routing/outbound/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Outbound Routes use ID for navigation
      return `/pbx/calls-routing/outbound/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData,
      patterns,
      gateways
    }),
    storageKey: 'outboundRoutes:saveAndNewData'
  });

  // Navigate to list (used by header back button if needed)
  const handleGoToList = () => {
    navigate('/pbx/calls-routing/outbound');
  };

  // Standard: Cancel resets form to original values (does NOT navigate)
  const handleCancel = () => {
    if (!isEditMode) {
      // In create mode, reset all to defaults
      setFormData({ ...DEFAULT_FORM_DATA });
      setPatterns([]);
      setGateways([]);
      setErrors({});
    } else {
      // In edit mode, restore to original values
      setFormData({ ...originalData });
      setPatterns(clonePatterns(originalPatterns));
      setGateways(cloneGateways(originalGateways));
      setErrors({});
    }
  };

  // Standard: Delete item with confirmation, then navigate to list
  const handleDeleteClick = () => {
    if (!isEditMode || !id) return;
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteOutboundRouteById(id);
      showSuccess(t('outboundRoutes.messages.deleteSuccess') || 'Outbound route deleted successfully');
      setShowDeleteModal(false);
      // Navigate to list after successful deletion
      navigate('/pbx/calls-routing/outbound');
    } catch (error) {
      const msg = error instanceof Error ? error.message : (t('outboundRoutes.messages.deleteError') || 'Failed to delete outbound route');
      showError(msg);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToOutboundRouteDuplicate(navigate, id);
  }, [id, isEditMode, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/calls-routing/outbound/new');
  }, [navigate]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'name',
        sortOrder: 'asc',
      });

      const response = await fetch(buildApiUrl(`/api/telephony/outbound-routes?${params.toString()}`), {
        headers: getApiHeaders(),
      });

      if (!response.ok) {
        throw new Error();
      }

      const result = await response.json();
      const items = Array.isArray(result?.data)
        ? (result.data as OutboundRoute[])
        : [];
      setQuickListItems(items);
    } catch {
      showError(t('outboundRoutes.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [showError, t]);

  const filteredQuickList = useMemo(() => {
    const items = quickListItems.filter(i => i.id != null);
    if (!quickListSearch.trim()) {
      return items;
    }
    const searchLower = quickListSearch.trim().toLowerCase();
    return items.filter((item) =>
      item.name.toLowerCase().includes(searchLower)
    );
  }, [quickListItems, quickListSearch]);

  // Handle field changes
  const handleChange = <K extends keyof OutboundRouteFormState>(field: K, value: OutboundRouteFormState[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Pattern handlers
  const addPattern = () => {
    setPatterns(prev => resequencePatterns([...prev, newPattern(prev.length + 1)]));
  };

  const updatePattern = <K extends keyof OutboundRoutePattern>(index: number, field: K, value: OutboundRoutePattern[K]) => {
    setPatterns(prev => prev.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const removePattern = (index: number) => {
    setPatterns(prev => resequencePatterns(prev.filter((_, i) => i !== index)));
  };

  const handlePatternDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setPatterns((items) => {
        const oldIndex = items.findIndex((_, i) => i === Number(active.id));
        const newIndex = items.findIndex((_, i) => i === Number(over.id));
        return resequencePatterns(arrayMove(items, oldIndex, newIndex));
      });
    }
  };

  // Gateway handlers
  const addGateway = () => {
    if (availableGateways.length === 0) {
      showWarning(t('outboundRoutes.messages.noGatewaysAvailable'));
      return;
    }

    // Find gateways not already added (use String comparison for Sqid string IDs)
    const usedGatewayIds = new Set(gateways.map(g => String(g.gatewayId)).filter(s => s && s !== '0' && s !== 'undefined' && s !== 'null'));
    const unusedGateways = availableGateways.filter(g => !usedGatewayIds.has(String(g.id)));

    if (unusedGateways.length === 0) {
      showWarning(t('outboundRoutes.messages.allGatewaysAssigned'));
      return;
    }

    // If only one gateway available, select it automatically
    // If multiple gateways available, leave it null so user must select
    const shouldAutoSelect = unusedGateways.length === 1;
    const selectedGateway = shouldAutoSelect ? unusedGateways[0] : null;

    setGateways(prev =>
      resequenceGateways([
        ...prev,
        {
          ...newGateway(prev.length + 1),
          // Store Sqid string ID as-is (gateway IDs from API are Sqid strings)
          gatewayId: Number(selectedGateway?.id ?? 0),
          gatewayName: selectedGateway?.name || '',
        },
      ]),
    );
  };

  const updateGateway = <K extends keyof OutboundRouteGateway>(index: number, field: K, value: OutboundRouteGateway[K]) => {
    setGateways(prev => {
      const newGateways = prev.map((g, i) => {
        if (i === index) {
          const updated = { ...g, [field]: value };
          // If changing gatewayId, use String comparison (IDs may be Sqid strings)
          if (field === 'gatewayId') {
            const strId = String(value);
            const gateway = availableGateways.find(ag => String(ag.id) === strId);
            updated.gatewayName = gateway?.name || '';
          }
          return updated;
        }
        return g;
      });
      console.log('[GW-DEBUG] new gateways state:', newGateways.map(g => ({ gatewayId: g.gatewayId, gatewayName: g.gatewayName })));
      return newGateways;
    });
  };

  const removeGateway = (index: number) => {
    setGateways(prev => resequenceGateways(prev.filter((_, i) => i !== index)));
  };

  const handleGatewayDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setGateways((items) => {
        const oldIndex = items.findIndex((_, i) => i === Number(active.id));
        const newIndex = items.findIndex((_, i) => i === Number(over.id));
        return resequenceGateways(arrayMove(items, oldIndex, newIndex));
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-20">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<ArrowRightLeft className="h-5 w-5 text-primary" />}
        backButtonText={t('outboundRoutes.actions.list')}
        onBack={() => navigate('/pbx/calls-routing/outbound')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('outboundRoutes.actions.quickList'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('outboundRoutes.actions.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('outboundRoutes.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      {/* Form */}
      <div className="mt-8">
        <form onSubmit={handleSubmit}>
          <OutboundRouteGeneralSection
            formData={formData}
            errors={errors}
            debugModeActive={debugModeActive}
            onFieldChange={handleChange}
            t={t}
          />


          {/* Tabs for Patterns and Gateways */}
          <OutboundRouteTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            patterns={patterns}
            gateways={gateways}
            availableGateways={availableGateways}
            errors={errors}
            routingStrategy={formData.routingStrategy}
            sensors={sensors}
            onPatternDragEnd={handlePatternDragEnd}
            onGatewayDragEnd={handleGatewayDragEnd}
            onUpdatePattern={updatePattern}
            onRemovePattern={removePattern}
            onAddPattern={addPattern}
            onUpdateGateway={updateGateway}
            onRemoveGateway={removeGateway}
            onAddGateway={addGateway}
            t={t}
          />

          {/* Fixed Action Bar */}
          <FixedActionBar
            onSaveWithAction={handleSaveWithAction}
            onCancel={handleCancel}
            onDelete={handleDeleteClick}
            isEditMode={isEditMode}
            isSaving={isSaving}
            isLoading={isLoading}
            saveText={t('outboundRoutes.actions.save')}
            cancelText={t('outboundRoutes.actions.cancel')}
            deleteText={t('common.delete')}
            useSaveDropdown={true}
            saveDropdownStorageKey="outboundRoutes:saveActionPreference"
          />
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('outboundRoutes.messages.confirmDeleteTitle') || 'Delete Outbound Route'}
        message={t('outboundRoutes.messages.confirmDeleteMessage', { name: formData.name || 'Outbound Route' }) || `Are you sure you want to delete "${formData.name || 'Outbound Route'}"?`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('outboundRoutes.quickList.title', 'Outbound Routes')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/calls-routing/outbound/${item.uuid ?? item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/calls-routing/outbound/${item.uuid ?? item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
          </button>
        )}
        searchPlaceholder={t('outboundRoutes.quickList.searchPlaceholder', 'Search by name')}
        emptyMessage={t('outboundRoutes.quickList.empty', 'No outbound routes found')}
        headerIcon={<ArrowRightLeft className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
